import { useState, useEffect, useMemo } from 'react';
import {
  Radio as MuiRadio,
  FormControlLabel as MuiFormControlLabel,
} from '@mui/material';
import Accordion from 'components/Accordion';
import { useTranslation } from 'react-i18next';
import { parseISO, subDays, isValid } from 'date-fns';
import { differenceInDays } from 'date-fns';

const now = new Date();

// Static activity list for backward compatibility (without translations)
export const activityList = [
  {
    noActivity: false,
    max: null,
    min: subDays(now, 1),
    label: '< 1 day ago',
    key: 'today',
  },
  {
    noActivity: false,
    max: subDays(now, 1),
    min: subDays(now, 3),
    label: '1-3 days ago',
    key: 'days',
  },
  {
    noActivity: false,
    max: subDays(now, 3),
    min: subDays(now, 7),
    label: '3-7 days ago',
    key: 'week',
  },
  {
    noActivity: false,
    max: subDays(now, 7),
    min: subDays(now, 30),
    label: '7-30 days ago',
    key: 'month',
  },
  {
    noActivity: false,
    max: subDays(now, 30),
    min: null,
    label: '30+ days ago',
    key: 'more',
  },
  {
    noActivity: true,
    max: null,
    min: null,
    label: 'No activity',
    key: 'no-activity',
  },
];

// Translated activity list function
export const getActivityList = (t) => [
  {
    noActivity: false,
    max: null,
    min: subDays(now, 1),
    label: t('< 1 day ago'),
    key: 'today',
  },
  {
    noActivity: false,
    max: subDays(now, 1),
    min: subDays(now, 3),
    label: t('1-3 days ago'),
    key: 'days',
  },
  {
    noActivity: false,
    max: subDays(now, 3),
    min: subDays(now, 7),
    label: t('3-7 days ago'),
    key: 'week',
  },
  {
    noActivity: false,
    max: subDays(now, 7),
    min: subDays(now, 30),
    label: t('7-30 days ago'),
    key: 'month',
  },
  {
    noActivity: false,
    max: subDays(now, 30),
    min: null,
    label: t('30+ days ago'),
    key: 'more',
  },
  {
    noActivity: true,
    max: null,
    min: null,
    label: t('No activity'),
    key: 'no-activity',
  },
];

export const determineActivityKey = (filters) => {
  
  // Check if we have a stored activity key from URL
  if (filters._activityKey) {
    return filters._activityKey;
  }
  
  if (filters.noActivity) return 'no-activity';

  if (!filters.lastActivity) return '';

  const { max, min } = filters.lastActivity || {};

  return (
    activityList.find(
      (item) =>
        (item.max === null ||
          (max && item.max.getTime() === new Date(max).getTime())) &&
        (item.min === null ||
          (min && item.min.getTime() === new Date(min).getTime()))
    )?.key || ''
  );
};

export default function ActivityFilter({ setFilters, filters, defaultExpanded = false }) {
  const { t } = useTranslation();
  const currKey = useMemo(() => determineActivityKey(filters), [filters]);
  const activityList = useMemo(() => getActivityList(t), [t]);

  const select = (key) => {
    // Allow deselection if clicking the same radio button
    if (key === currKey) {
      setFilters((curr) => ({
        ...curr,
        lastActivity: undefined,
        noActivity: undefined,
        _activityKey: undefined, // Clear URL-stored key
      }));
      return;
    }

    const selectedActivity = activityList.find((item) => item.key === key);

    if (!selectedActivity) {
      // Handle the case where no valid activity is selected
      setFilters((curr) => ({
        ...curr,
        lastActivity: undefined,
        noActivity: undefined,
        _activityKey: undefined, // Clear URL-stored key
      }));
    } else {
      setFilters((curr) => ({
        ...curr,
        lastActivity: selectedActivity?.noActivity
          ? undefined
          : {
              max: selectedActivity.max ? selectedActivity.max : null,
              min: selectedActivity.min ? selectedActivity.min : null,
            },
        noActivity: selectedActivity?.noActivity || undefined,
        _activityKey: undefined, // Clear URL-stored key when manually selecting
      }));
    }
  };

  return (
    <Accordion title={t('Activity')} defaultExpanded={defaultExpanded}>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {activityList.map((item) => {
          return (
            <MuiFormControlLabel
              key={item.key}
              control={
                <MuiRadio
                  checked={item.key === currKey}
                  onChange={() => select(item.key)}
                  name="activity-filter"
                />
              }
              label={item.label}
            />
          );
        })}
      </div>
    </Accordion>
  );
}
